"use client";
import React, { useCallback, useRef, useState } from "react";

import { migrateAuthToken, setCookieAccessToken } from "@/app/actions";
import { isClient } from "@/utils";
import { useOnce } from "@/utils/hooks/useOnce";

import { setClientAccessToken } from "./AuthProvider.utils";

interface AuthContextType {
  accessToken: string | null;
  setAccessToken: (accessToken: string | null) => Promise<void>;
}

export const AuthContext = React.createContext<AuthContextType>({
  accessToken: null,
  setAccessToken: () => Promise.resolve(),
});

interface AuthProviderProps {
  accessToken: string | null;
  children: React.ReactNode;
}

/**
 * AuthProvider
 * @description
 * AuthProviderは、アクセストークンを管理するためのコンテキストプロバイダーである。
 * 4つの役割を持つ。
 * - accessTokenをuseStateで保存し、Context、hooksを利用して、Reactコンポーネント全体で利用できるようにする
 *   => setAccessTokenState
 * - API関数など静的な関数内でもアクセストークンを利用できるようにする
 *   => setClientAccessToken
 * - アクセストークンをNextJSのサーバーアクションを利用して、Cookieで永続化する
 *   => setCookieAccessToken
 * - 古いトークンを新しいトークンに置き換えるためのマイグレーション処理を行う
 *   => migrateAuthToken
 * @returns
 */
export const AuthProvider = ({ accessToken: initialAccessToken, children }: AuthProviderProps) => {
  const [accessToken, setAccessTokenState] = useState<string | null>(initialAccessToken);
  const initializedRef = useRef<boolean>(false);

  if (!initializedRef.current && isClient) {
    setClientAccessToken(initialAccessToken);
    initializedRef.current = true;
  }

  useOnce(() => {
    // 外部サイト → 自サイトへのリンククリックの場合: ブラウザがサーバーにCookieを送信しないため
    // クライアントサイドで明示的に、サーバーサイドで取得しなおし、stateにセットする
    // 2025/05/28で（SameSite: Strict）から（SameSite: Lax）に変更したため、この処理は不要になるが、
    // すでにSameSite: Strictで動作しているため、1ヶ月間はこの処理を維持する
    migrateAuthToken().then((token) => {
      setClientAccessToken(token);
      setAccessTokenState(token);
    });
  });

  const setAccessToken = useCallback(async (accessToken: string | null) => {
    setClientAccessToken(accessToken);
    setAccessTokenState(accessToken);
    await setCookieAccessToken(accessToken);
  }, []);

  return (
    <AuthContext.Provider value={{ accessToken, setAccessToken }}>{children}</AuthContext.Provider>
  );
};
